import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Link } from '@sorare/routing';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS, Text14 } from '@sorare/core/src/atoms/typography';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { Block } from '../Block';
import { ProfileCta } from '../ProfileCta';
import { CardRewards } from './CardRewards';
import { Pro_trophiesSummary } from './__generated__/index.graphql';
import proBadge from './assets/pro-badge.svg';

export type Props = {
  trophiesSummary: Pro_trophiesSummary;
  historyUrl: string;
  rewardsPoolUrl?: string;
};

export const Pro = ({ trophiesSummary, historyUrl, rewardsPoolUrl }: Props) => {
  return (
    <Block
      title={
        <FormattedMessage id="ManagerProfile.Pro.pro" defaultMessage="Pro" />
      }
      icon={<img src={proBadge} alt="Pro" />}
      stat={{
        count: trophiesSummary.finalRankings,
        label: (
          <FormattedMessage
            id="ManagerProfile.Pro.competitionsPlayed"
            defaultMessage="{count, plural, one {competition} other {competitions}} played"
            values={{ count: trophiesSummary.finalRankings }}
          />
        ),
      }}
      cta={
        <Vertical center>
          <ProfileCta to={historyUrl}>
            <FormattedMessage
              id="ManagerProfile.Pro.seeProHistory"
              defaultMessage="See Pro history"
            />
          </ProfileCta>
          {rewardsPoolUrl && (
            <Link to={rewardsPoolUrl}>
              <LabelS as="p" color="var(--c-brand-300)">
                <FormattedMessage {...fantasy.viewRewardsPool} />
              </LabelS>
            </Link>
          )}
        </Vertical>
      }
    >
      <Text14 color="var(--c-neutral-500)">
        <FormattedMessage {...glossary.rewards} />
      </Text14>
      <CardRewards cardRewards={trophiesSummary} />
    </Block>
  );
};

Pro.fragments = {
  trophiesSummary: gql`
    fragment Pro_trophiesSummary on TrophiesSummary {
      limited: cardRewards(rarity: limited)
      rare: cardRewards(rarity: rare)
      super_rare: cardRewards(rarity: super_rare)
      unique: cardRewards(rarity: unique)
      top1: podiumRankings(ranking: 1)
      top2: podiumRankings(ranking: 2)
      top3: podiumRankings(ranking: 3)
      finalRankings
    }
  ` as TypedDocumentNode<Pro_trophiesSummary>,
};
